/**
 * Design tokens for the Data Quality console.
 *
 * ── Why these live here and not in `charts/chartTheme.ts` ────────────────────
 * `chartTheme.ts` is deliberately a single brand-purple family, validated for
 * colour-vision-deficiency separation, and 43 chart files depend on it. Adding
 * red/amber "state" colours to it would break that contract for every one of
 * them. So the split is:
 *
 *   - STATE colour (severity, status, origin, validation, AI) → this file.
 *   - CHART CHROME (ink, type sizes, grid/axis/legend prop bags, bar radius)
 *     → imported from `chartTheme.ts`, never redefined here.
 *
 * ── House rule: state is never colour alone ──────────────────────────────────
 * Every token carries a `label` (a word) and a `glyph` (an icon or character)
 * alongside its colour, because a colour-blind reader, a greyscale print and a
 * screen reader all need the state without the hue. Consumers must render at
 * least two of the three.
 *
 * No JSX in this file so it stays importable from anywhere; lucide icons are
 * referenced as components, never rendered.
 */

import {
  AlertTriangle,
  Calculator,
  CheckCircle2,
  Info,
  Link2,
  MinusCircle,
  Sparkles,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import type {
  AiStatus,
  FieldOrigin,
  FieldValidationState,
  IssueSeverity,
  ValidationStatus,
} from '../../../types/dataQuality';

/** The shape every state token shares. `hex` is for SVG; the rest for HTML. */
export interface StateToken {
  /** The word. Always rendered somewhere — never colour alone. */
  label: string;
  /** Raw colour, for SVG strokes and fills where a class cannot reach. */
  hex: string;
  /** `bg-*` for a dot or a bar segment. */
  dot: string;
  /** Full chip classes: background + text + (where relevant) border. */
  chip: string;
  /** Text-only colour, for a label sitting on the page background. */
  text: string;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Severity — one finding's weight
 * ──────────────────────────────────────────────────────────────────────────── */

export interface SeverityToken extends StateToken {
  icon: LucideIcon;
  /** Plural form, for counts: "3 warnings". */
  plural: string;
  /** Sort weight, worst first. */
  rank: number;
}

/**
 * `info` is brand purple (`colors.info`), NOT the stock `blue-400` the old
 * panel used — that blue belonged to no palette in this app and collided with
 * the three other blues already on the Overview tab.
 */
export const SEVERITY_TOKENS: Record<IssueSeverity, SeverityToken> = {
  error: {
    label: 'Error',
    plural: 'errors',
    hex: '#ef4444', // colors.error
    dot: 'bg-red-500',
    chip: 'bg-red-50 text-red-700 border border-red-200',
    text: 'text-red-600',
    icon: XCircle,
    rank: 0,
  },
  warning: {
    label: 'Warning',
    plural: 'warnings',
    hex: '#f59e0b', // colors.warning
    dot: 'bg-amber-500',
    chip: 'bg-amber-50 text-amber-800 border border-amber-200',
    text: 'text-amber-600',
    icon: AlertTriangle,
    rank: 1,
  },
  info: {
    label: 'Note',
    plural: 'notes',
    hex: '#9679e7', // colors.info — brand purple
    dot: 'bg-secondary-500',
    chip: 'bg-secondary-50 text-secondary-800 border border-secondary-200',
    text: 'text-secondary-700',
    icon: Info,
    rank: 2,
  },
};

export const SEVERITY_ORDER: IssueSeverity[] = ['error', 'warning', 'info'];

/* ────────────────────────────────────────────────────────────────────────────
 * Chain status — the badge on a source
 * ──────────────────────────────────────────────────────────────────────────── */

export interface StatusToken extends StateToken {
  icon: LucideIcon;
  /** Maps onto the shared `ui/Badge` variants. */
  badge: 'success' | 'warning' | 'error';
}

/**
 * `clean` is `colors.success` (#10b981), not the `#22c55e` the old gauge used —
 * that value appears nowhere in `tailwind.config.js`.
 */
export const STATUS_TOKENS: Record<ValidationStatus, StatusToken> = {
  clean: {
    label: 'Validated',
    hex: '#10b981', // colors.success
    dot: 'bg-emerald-500',
    chip: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    text: 'text-emerald-600',
    icon: CheckCircle2,
    badge: 'success',
  },
  warnings: {
    label: 'Warnings',
    hex: '#f59e0b',
    dot: 'bg-amber-500',
    chip: 'bg-amber-50 text-amber-800 border border-amber-200',
    text: 'text-amber-600',
    icon: AlertTriangle,
    badge: 'warning',
  },
  errors: {
    label: 'Errors',
    hex: '#ef4444',
    dot: 'bg-red-500',
    chip: 'bg-red-50 text-red-700 border border-red-200',
    text: 'text-red-600',
    icon: XCircle,
    badge: 'error',
  },
};

/* ────────────────────────────────────────────────────────────────────────────
 * Score bands
 * ──────────────────────────────────────────────────────────────────────────── */

export type ScoreBandKey = 'strong' | 'adequate' | 'weak' | 'poor';

export interface ScoreBand {
  key: ScoreBandKey;
  /**
   * "Strong", never "Perfect". Scoring v2 clamps at 0.97, so the top of the
   * range is a ceiling rather than a flawless result, and a word that implies
   * perfection would misdescribe every good company in the system.
   */
  label: string;
  hex: string;
  text: string;
}

const SCORE_BANDS: ScoreBand[] = [
  { key: 'strong', label: 'Strong', hex: '#10b981', text: 'text-emerald-600' },
  { key: 'adequate', label: 'Adequate', hex: '#7144c9', text: 'text-secondary-700' },
  { key: 'weak', label: 'Weak', hex: '#f59e0b', text: 'text-amber-600' },
  { key: 'poor', label: 'Poor', hex: '#ef4444', text: 'text-red-600' },
];

/**
 * Band a 0-1 score.
 *
 * The ring is coloured by THIS, not by `status`. A chain can hold no findings
 * at all and still score 0.45, because the score is mostly coverage — painting
 * that ring green because nothing failed would tell the reader the opposite of
 * what the number says. The status badge sits beside the ring and carries the
 * findings verdict separately.
 */
export function scoreBand(score: number | null | undefined): ScoreBand {
  if (score == null || !Number.isFinite(score)) return SCORE_BANDS[3];
  if (score >= 0.9) return SCORE_BANDS[0];
  if (score >= 0.75) return SCORE_BANDS[1];
  if (score >= 0.5) return SCORE_BANDS[2];
  return SCORE_BANDS[3];
}

/* ────────────────────────────────────────────────────────────────────────────
 * Field origin — where a number came from
 *
 * INDEPENDENT of the validation axis: a derived value can be validated or
 * failed. Seven values because the contract has seven, even though live data
 * currently shows only reported / defaulted / absent.
 * ──────────────────────────────────────────────────────────────────────────── */

export interface OriginToken extends StateToken {
  /** One line explaining what the origin means, for a tooltip or legend. */
  hint: string;
}

export const ORIGIN_TOKENS: Record<FieldOrigin, OriginToken> = {
  reported: {
    label: 'Reported',
    hex: '#10b981',
    dot: 'bg-emerald-500',
    chip: 'bg-emerald-50 text-emerald-700',
    text: 'text-emerald-700',
    hint: 'Supplied directly by the source.',
  },
  derived: {
    label: 'Derived',
    hex: '#ad91ed', // secondary-400
    dot: 'bg-secondary-400',
    chip: 'bg-secondary-50 text-secondary-700',
    text: 'text-secondary-700',
    hint: 'Computed from other reported figures.',
  },
  estimated: {
    label: 'Estimated',
    hex: '#8154dd', // secondary-600
    dot: 'bg-secondary-600',
    chip: 'bg-secondary-100 text-secondary-800',
    text: 'text-secondary-800',
    hint: 'Approximated rather than measured.',
  },
  defaulted: {
    label: 'Defaulted',
    hex: '#f59e0b',
    dot: 'bg-amber-500',
    chip: 'bg-amber-50 text-amber-800',
    text: 'text-amber-700',
    // The dangerous column: a zero that looks like data and silently corrupts
    // every ratio built on it.
    hint: 'Zero substituted for a missing value — can distort any ratio built on it.',
  },
  absent: {
    label: 'Absent',
    hex: '#d4d4d4', // neutral-300
    dot: 'bg-neutral-300',
    chip: 'bg-neutral-100 text-neutral-500',
    text: 'text-neutral-500',
    hint: 'Not supplied by this source. An absence, not a failure.',
  },
  invalid: {
    label: 'Invalid',
    hex: '#ef4444',
    dot: 'bg-red-500',
    chip: 'bg-red-50 text-red-700',
    text: 'text-red-600',
    hint: 'Present but not a usable number.',
  },
  reconstructed: {
    label: 'Reconstructed',
    hex: '#b5bdd6', // primary-300
    dot: 'bg-primary-300',
    chip: 'bg-primary-50 text-primary-700',
    text: 'text-primary-700',
    hint: 'Rebuilt from its components.',
  },
};

/* ────────────────────────────────────────────────────────────────────────────
 * Field validation state — what the checks concluded
 * ──────────────────────────────────────────────────────────────────────────── */

export interface ValidationToken extends StateToken {
  /** A character, not an icon: it sits inside dense table cells. */
  glyph: string;
  /** The bar fill for a per-statement rollup. */
  bar: string;
}

export const VALIDATION_TOKENS: Record<FieldValidationState, ValidationToken> = {
  validated: {
    label: 'Validated',
    glyph: '✓',
    hex: '#10b981',
    dot: 'bg-emerald-500',
    chip: 'bg-emerald-50 text-emerald-700',
    text: 'text-emerald-600',
    bar: 'bg-emerald-500',
  },
  uncertain: {
    label: 'Uncertain',
    glyph: '!',
    hex: '#f59e0b',
    dot: 'bg-amber-500',
    chip: 'bg-amber-50 text-amber-800',
    text: 'text-amber-600',
    bar: 'bg-amber-500',
  },
  failed: {
    label: 'Failed',
    glyph: '✕',
    hex: '#ef4444',
    dot: 'bg-red-500',
    chip: 'bg-red-50 text-red-700',
    text: 'text-red-600',
    bar: 'bg-red-500',
  },
  not_reviewed: {
    label: 'Not reviewed',
    glyph: '–',
    hex: '#d4d4d4',
    dot: 'bg-neutral-300',
    chip: 'bg-neutral-100 text-neutral-500',
    text: 'text-neutral-500',
    bar: 'bg-neutral-300',
  },
};

/* ────────────────────────────────────────────────────────────────────────────
 * AI reviewer status
 *
 * Six states, deliberately not collapsed into a boolean. They mean six
 * different things about how far to trust the score beside them.
 * ──────────────────────────────────────────────────────────────────────────── */

export type AiTone = 'reviewed' | 'healthy' | 'degraded';

export interface AiStatusToken {
  label: string;
  /**
   * `reviewed` — it ran. `healthy` — it correctly did not run (a good outcome,
   * never an empty state). `degraded` — it should have run and did not, so the
   * score beside it has had less scrutiny. Degraded is AMBER, never red: a
   * missing AI opinion does not invalidate the deterministic checks.
   */
  tone: AiTone;
  /** Sentence shown to the reader. Written to stand alone. */
  message: string;
  icon: LucideIcon;
  chip: string;
}

export const AI_STATUS_TOKENS: Record<AiStatus, AiStatusToken> = {
  completed: {
    label: 'AI reviewed',
    tone: 'reviewed',
    message: 'An AI reviewer examined the ambiguous findings and recorded a verdict on each.',
    icon: Sparkles,
    chip: 'bg-secondary-50 text-secondary-800 border border-secondary-200',
  },
  not_needed: {
    label: 'Review not required',
    tone: 'healthy',
    message:
      'AI review not required — every finding was unambiguous, so the rule-based score is final.',
    icon: CheckCircle2,
    chip: 'bg-neutral-50 text-neutral-600 border border-neutral-200',
  },
  not_enabled: {
    label: 'Review not enabled',
    tone: 'healthy',
    message: 'AI review is not enabled for this source; the score is rule-based.',
    icon: CheckCircle2,
    chip: 'bg-neutral-50 text-neutral-600 border border-neutral-200',
  },
  unavailable: {
    label: 'Review unavailable',
    tone: 'degraded',
    message: 'AI review was unavailable for this run — the score shown is rule-based only.',
    icon: AlertTriangle,
    chip: 'bg-amber-50 text-amber-800 border border-amber-200',
  },
  rejected: {
    label: 'Review discarded',
    tone: 'degraded',
    message: 'The AI review was discarded for this run — the score shown is rule-based only.',
    icon: AlertTriangle,
    chip: 'bg-amber-50 text-amber-800 border border-amber-200',
  },
  failed: {
    label: 'Review failed',
    tone: 'degraded',
    message: 'AI review could not be completed — the score shown is rule-based only.',
    icon: AlertTriangle,
    chip: 'bg-amber-50 text-amber-800 border border-amber-200',
  },
};

/* ────────────────────────────────────────────────────────────────────────────
 * AI verdicts — what a reviewer concluded about one finding
 * ──────────────────────────────────────────────────────────────────────────── */

export type AiVerdict = 'likely_extraction_error' | 'likely_genuine' | 'insufficient_evidence';

export interface VerdictToken extends StateToken {
  /**
   * What the verdict does to the score, in words. Spelled out because the
   * direction is counter-intuitive: agreeing the figure is real RELIEVES the
   * penalty, and calling it an extraction error AMPLIFIES it.
   */
  effect: string;
}

export const VERDICT_TOKENS: Record<AiVerdict, VerdictToken> = {
  likely_extraction_error: {
    label: 'Likely extraction error',
    hex: '#ef4444',
    dot: 'bg-red-500',
    chip: 'bg-red-50 text-red-700',
    text: 'text-red-600',
    effect: 'increases the penalty',
  },
  likely_genuine: {
    label: 'Likely genuine',
    hex: '#10b981',
    dot: 'bg-emerald-500',
    chip: 'bg-emerald-50 text-emerald-700',
    text: 'text-emerald-700',
    effect: 'reduces the penalty',
  },
  insufficient_evidence: {
    label: 'Insufficient evidence',
    hex: '#a3a3a3', // neutral-400
    dot: 'bg-neutral-400',
    chip: 'bg-neutral-100 text-neutral-600',
    text: 'text-neutral-600',
    effect: 'leaves the score unchanged',
  },
};

export const VERDICT_ORDER: AiVerdict[] = [
  'likely_extraction_error',
  'insufficient_evidence',
  'likely_genuine',
];

/** Verdicts arrive as free strings on the wire; unknown values get no token. */
export function verdictToken(verdict: string | null | undefined): VerdictToken | null {
  if (!verdict) return null;
  return VERDICT_TOKENS[verdict as AiVerdict] ?? null;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Source status — how a citation was established
 *
 * `derived` is the one value worth naming explicitly: it carries no page
 * number and no provider because it isn't a citation of a location at all —
 * it's the backend's own sentence explaining a computation. Confusing it with
 * `verified` (an actual OCR excerpt) is exactly the mistake the old plain-text
 * "· verified" / "· derived" suffix invited, since both rendered identically.
 * ──────────────────────────────────────────────────────────────────────────── */

export type SourceStatus =
  | 'verified'
  | 'derived'
  | 'mapped'
  | 'defaulted'
  | 'not_found'
  | 'skipped';

export interface SourceStatusToken extends StateToken {
  icon: LucideIcon;
  /** The citation card's left accent border — colour visible before a word is read. */
  accent: string;
  /**
   * Soft background wash for the card's header strip. Also used for the BODY
   * on a `derived` card, so a computed reconciliation reads as one cohesive
   * tinted unit rather than a plain white card with a small badge in the
   * corner — the distinction a reader needs to notice at a glance, not on
   * close reading.
   */
  tint: string;
}

export const SOURCE_STATUS_TOKENS: Record<SourceStatus, SourceStatusToken> = {
  verified: {
    label: 'Verified',
    icon: CheckCircle2,
    hex: '#10b981',
    dot: 'bg-emerald-500',
    chip: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    text: 'text-emerald-700',
    accent: 'border-l-emerald-400',
    tint: 'bg-emerald-50/50',
  },
  derived: {
    // Indigo, deliberately not the secondary-purple this same dossier uses
    // for "AI opinion" — a deterministic reconciliation is not a model's
    // opinion and must not be mistaken for one.
    label: 'Computed',
    icon: Calculator,
    hex: '#6366f1',
    dot: 'bg-indigo-500',
    chip: 'bg-indigo-50 text-indigo-700 border border-indigo-200',
    text: 'text-indigo-700',
    accent: 'border-l-indigo-400',
    tint: 'bg-indigo-50/50',
  },
  mapped: {
    label: 'Mapped',
    icon: Link2,
    hex: '#737373', // neutral-500
    dot: 'bg-neutral-400',
    chip: 'bg-neutral-100 text-neutral-600 border border-neutral-200',
    text: 'text-neutral-600',
    accent: 'border-l-neutral-300',
    tint: 'bg-neutral-50',
  },
  defaulted: {
    label: 'Defaulted',
    icon: AlertTriangle,
    hex: '#f59e0b',
    dot: 'bg-amber-500',
    chip: 'bg-amber-50 text-amber-800 border border-amber-200',
    text: 'text-amber-700',
    accent: 'border-l-amber-400',
    tint: 'bg-amber-50/50',
  },
  not_found: {
    label: 'Not found',
    icon: XCircle,
    hex: '#ef4444',
    dot: 'bg-red-500',
    chip: 'bg-red-50 text-red-700 border border-red-200',
    text: 'text-red-700',
    accent: 'border-l-red-400',
    tint: 'bg-red-50/50',
  },
  skipped: {
    label: 'Skipped',
    icon: MinusCircle,
    hex: '#a3a3a3', // neutral-400
    dot: 'bg-neutral-300',
    chip: 'bg-neutral-100 text-neutral-500 border border-neutral-200',
    text: 'text-neutral-500',
    accent: 'border-l-neutral-200',
    tint: 'bg-neutral-50',
  },
};

/** Statuses arrive as free strings on the wire; unknown values get no token. */
export function sourceStatusToken(status: string | null | undefined): SourceStatusToken | null {
  if (!status) return null;
  return SOURCE_STATUS_TOKENS[status as SourceStatus] ?? null;
}
